import { createContext, useContext, useEffect, useState } from "react";

// Çeviriler artık bu dosyanın içinde gömülü değil, dil başına ayrı JSON'da
// (src/locales/*.json). 2.200 satırlık iç içe nesne 7 dile çıkınca okunamaz
// hâle geliyordu; ayrıca çevirmenlerin JS'e dokunmadan çalışabilmesi gerek.
import ar from "./locales/ar.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import hi from "./locales/hi.json";
import id from "./locales/id.json";
import pl from "./locales/pl.json";
import ptBR from "./locales/pt-br.json";
import tr from "./locales/tr.json";

const translations = { tr, en, hi, id, "pt-br": ptBR, ar, pl, es };

/** Dil seçicide gösterilecek liste — her dil kendi yazımıyla. */
export const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
  { code: "id", label: "Bahasa Indonesia", flag: "🇮🇩" },
  { code: "pt-br", label: "Português", flag: "🇧🇷" },
  { code: "ar", label: "العربية", flag: "🇪🇬" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
];

/** Sağdan sola yazılan diller. */
export const RTL_LANGUAGES = ["ar"];

const SUPPORTED = Object.keys(translations);

/** "pt-BR", "hi-IN", "PT" gibi değerleri desteklenen bir koda indirger. */
export function normalizeLang(raw) {
  if (!raw || typeof raw !== "string") return null;
  const c = raw.trim().toLowerCase().replace("_", "-");
  if (SUPPORTED.includes(c)) return c;
  if (c === "pt" || c.startsWith("pt-")) return "pt-br";
  const base = c.split("-")[0];
  return SUPPORTED.includes(base) ? base : null;
}

/**
 * Açılışta kullanılacak dil.
 *
 * Sıra: kullanıcının önceki seçimi → cihazın dili → derleme varsayılanı.
 * Cihaz dili önemli: Hindistan'daki biri uygulamayı ilk açtığında karşısına
 * Türkçe çıkmamalı.
 */
function initialLang() {
  try {
    const saved =
      normalizeLang(localStorage.getItem("lang")) ||
      normalizeLang(localStorage.getItem("sk_lang"));
    if (saved) return saved;
  } catch (e) {
    // Depolama kapalı (gizli sekme) — cihaz diline düşeriz.
  }

  const candidates = [];
  if (typeof navigator !== "undefined") {
    if (navigator.languages) candidates.push(...navigator.languages);
    if (navigator.language) candidates.push(navigator.language);
  }
  for (const c of candidates) {
    const hit = normalizeLang(c);
    if (hit) return hit;
  }

  return normalizeLang(process.env.REACT_APP_DEFAULT_LANG) || "en";
}

const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
  isRTL: false,
});

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(initialLang);

  useEffect(() => {
    const isRTL = RTL_LANGUAGES.includes(lang);
    try {
      localStorage.setItem("lang", lang);
    } catch (e) {
      /* depolama kapalı */
    }

    document.documentElement.lang = lang;
    // Arapça'da tüm yerleşim sağdan sola döner. Tailwind'in mantıksal
    // yardımcıları (ms-*, me-*, text-start) bu özniteliği okuyor.
    document.documentElement.dir = isRTL ? "rtl" : "ltr";

    // Eski "sk_lang" sistemi (Landing / Messenger / Profile kendi kopyalarını
    // ondan okuyor) ile senkron kal — TEK anahtar tüm uygulamayı çevirsin.
    // O sayfalar yalnızca tr/en biliyor; diğer diller en'e eşlenir.
    try {
      const sk = lang === "tr" ? "tr" : "en";
      localStorage.setItem("sk_lang", sk);
      window.dispatchEvent(new StorageEvent("storage", { key: "sk_lang", newValue: sk }));
    } catch {
      /* StorageEvent desteklenmiyor — odak değişince eşitlenir */
    }
  }, [lang]);

  /**
   * Çeviri arama. Anahtar seçilen dilde yoksa İngilizceye, o da yoksa
   * anahtarın kendisine düşer — eksik bir çeviri boş ekran değil, en kötü
   * ihtimalle İngilizce metin demek.
   */
  const t = (path) => {
    const parts = String(path).split(".");
    for (const dict of [translations[lang], translations.en]) {
      let cur = dict;
      let ok = true;
      for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else {
          ok = false;
          break;
        }
      }
      if (ok && typeof cur === "string") return cur;
    }
    return path;
  };

  const value = {
    lang,
    setLang: (next) => setLang(normalizeLang(next) || "en"),
    t,
    isRTL: RTL_LANGUAGES.includes(lang),
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
